'use client';
import { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import moment from 'moment';
import Lottie from 'lottie-react';

import { PetskedTitle } from '@/components/Heading/PetskedTitle';
import { AppointmentInfoPanel } from '../components/AppointmentScheduleDetails';
import { AppointmentServiceSelection } from '../components/AppointmentServiceSelection';
import { AppointmentForm } from '../components/AppointmentForm';
import { AppointmentFormConfirmation } from '../components/AppointmentConfirmation';
import { BackNav } from '@/components/BackNavigation/BackNavigation';
import { ConsumerLogin } from '../components/ConsumerLogin';
import { ForgotPassword } from '../components/ForgotPassword';
import { ConsumerSignUp } from '../components/SignUp';
import { EmailVerification } from '../components/ConsumerEmailVerification';
import { ParentProfile } from '../components/ParentProfile';
import { PetProfile } from '../components/PetProfile';
import { CreditCardDetails } from '../components/CreditCardDetails';
import { BookingConfirmation } from '../components/BookingConfirmation';
import { CreditCardNonRegistration } from '../components/CreditCardNonRegistration';
import { NonRegistrationConfirmation } from '../components/NonRegistrationConfirmation';

import { useGetBussiness } from '@/features/availability/api/getBisiness';
import { useAuth } from '@/lib/auth';
import { BookAppointmentDTO, useBookAppointMents } from '../api/bookAppointment';
import { AppointmentData, Business } from '../types';
import storage from '@/utils/storage';

import appCompoLoading from '@/assets/animation/loading.json';
import 'react-phone-input-2/lib/style.css';
import './ScheduleAppoinmtent.scss';
import { UserContext } from '@/components/Layout';
import Utils from '@/utils/util';
import { SelectAddonServices } from '../components/SelectAddonServices';
import { ServiceTypeSelection } from '../components/ServiceTypeSelection';
import i18n from 'i18next';
import '../../../i18next.ts';
import { useTranslation } from 'react-i18next';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY as string);

export default function ScheduleAppointmentStripe() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [hostBusinessData, setHostBusinessData] = useState<Business>();
  const [currentPosition, setCurrentPosition] = useState<{ lat: number; lng: number }>({
    lat: 0,
    lng: 0,
  });
  const businessMutation = useGetBussiness();

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setCurrentPosition({ lat: latitude, lng: longitude });
      },
      (error) => {
        getBusinessData();
        console.error('Error obtaining location:', error);
      }
    );
    return () => {
      setHostBusinessData(undefined);
    };
  }, []);

  const getBusinessData = async () => {
    const res = await businessMutation.mutateAsync({ slugName: slug, lat: currentPosition.lat, lng: currentPosition.lng });
    setHostBusinessData(res);
  }

  useEffect(() => {
    if(currentPosition.lat && currentPosition.lng) {
      getBusinessData();
    }
  }, [currentPosition]);

  useEffect(() => {
    if (hostBusinessData) {
      if (hostBusinessData && hostBusinessData.id) {
        const pathArray = window.location.pathname.split('/');
        storage.setKey('slug', pathArray[1]);
        if(hostBusinessData && hostBusinessData?.locations?.length > 0) {
          const selectedLocation = hostBusinessData.locations[0];
          
          storage.setKey(`loc_id_${hostBusinessData.type === 'business' ? hostBusinessData.slug : selectedLocation.slug }`, selectedLocation.id);
        }
      }
      if(hostBusinessData.country) {
        const language = hostBusinessData?.country === 'New Zealand' ? 'en-NZ' : 'en-US';
        i18n.changeLanguage(language).then(() => {
          console.log(`Language changed to: ${i18n.language}`);
        });
      }
    }
  }, [hostBusinessData]);

  if (businessMutation.isLoading || !hostBusinessData) {
    return (
      <section id="main-scroll-container" className="petsked-container-bg non-registration">
        <div className="preloader-schedule-new">
          <Lottie className="loader" animationData={appCompoLoading} />
        </div>
      </section>
    );
  }
  return (
    <Elements stripe={stripePromise}>
      {hostBusinessData && <ScheduleAppointment hostBusinessData={hostBusinessData} />}
    </Elements>
  );
}
function ScheduleAppointment({ hostBusinessData }: { hostBusinessData: Business }) {
  const offerringService = Utils.getOfferingService(hostBusinessData?.offerings);
  const userContext = useContext(UserContext);
  const { user, refetchUser } = useAuth();
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState<
    | 'serviceTypeSelection'
    | 'serviceSelection'
    | 'addons-selection'
    | 'personalInfo'
    | 'confirmation'
    | 'login'
    | 'forgot-password'
    | 'email-verification'
    | 'sign-up'
    | 'sign-in'
    | 'parent-profile'
    | 'pet-profile'
    | 'appointment-confirmation'
    | 'payment-method'
    | 'capture-payment'
    | 'pre-confirmation'
  >(
    offerringService.count === 1
      ? 'serviceSelection'
      : offerringService.count > 1
      ? 'serviceTypeSelection'
      : 'serviceSelection'
  );
  const [appointmentData, setAppointmentData] = useState<AppointmentData>({
    selectedServiceType: offerringService.selectedOffering,
  });
  const bookAppointmentMutation = useBookAppointMents();

  const stripe = useStripe();
  
  useEffect(() => {
    if(!appointmentData.selectedLocation)  {
      if(hostBusinessData && hostBusinessData?.locations?.length > 0) {
        appointmentData.selectedLocation = hostBusinessData.locations[0]
      }
    }
    if (appointmentData?.selectedLocation?.registrationRequired && user) {
      const currentPage = updateCurrentPage();
      setCurrentPage(currentPage);
      if (appointmentData) {
        appointmentData.parentProfile = user;
        if (
          hostBusinessData.isStripeAccountVerified &&
          hostBusinessData.paymentMode === 'sandbox'
        ) {
          appointmentData.creditCard = user.defaultCardSandbox;
        } else {
          appointmentData.creditCard = user.defaultCard;
        }
      }
    }
    if (appointmentData) {
      appointmentData.business = hostBusinessData;
      if(!appointmentData.selectedServiceType && offerringService.selectedOffering) {
        appointmentData.selectedServiceType = offerringService.selectedOffering;
      }    
    }
    onAppointmentDataChange(appointmentData);
  }, [user, user?.pets, hostBusinessData, appointmentData.selectedLocation]);

  const backToPreviousPage = () => {
    if (currentPage == 'serviceSelection') {
      onAppointmentDataChange({
        selectedServiceType: appointmentData?.selectedServiceType,
        parentProfile: appointmentData.parentProfile,
        creditCard: appointmentData.creditCard,
        business: appointmentData.business,
        selectedLocation: appointmentData.selectedLocation,
      });
      setCurrentPage('serviceTypeSelection');
    } else if (currentPage == 'login') {
      if (appointmentData.addonsAvailable) {
        setCurrentPage('addons-selection');
      } else {
        setCurrentPage('serviceSelection');
      }
    } else if (
      currentPage == 'forgot-password' ||
      currentPage == 'sign-up' ||
      currentPage == 'email-verification'
    ) {
      setCurrentPage('login');
    } else if (currentPage == 'addons-selection') {
      setCurrentPage('serviceSelection');
    } else if (currentPage == 'pet-profile') {
      setCurrentPage('parent-profile');
    } else if (currentPage == 'pre-confirmation') {
      const servicePrice = appointmentData?.service?.price ? appointmentData?.service?.price : 0;
      if (servicePrice > 0) {
        setCurrentPage('capture-payment');
      } else {
        setCurrentPage('personalInfo');
      }
    } else if (currentPage == 'appointment-confirmation') {
      setCurrentPage('payment-method');
    } else if (currentPage == 'payment-method') {
      setCurrentPage('pet-profile');
    } else if (currentPage == 'personalInfo' && appointmentData.addonsAvailable) {
      setCurrentPage('addons-selection');
    } else {
      onAppointmentDataChange({
        selectedServiceType: appointmentData?.selectedServiceType,
        parentProfile: appointmentData.parentProfile,
        creditCard: appointmentData.creditCard,
        business: appointmentData.business,
        selectedLocation: appointmentData.selectedLocation,
      });
      setCurrentPage('serviceTypeSelection');
    }
  };
  const onLocationChange = (appointmentData: AppointmentData) => {
    onAppointmentDataChange(appointmentData);
    setCurrentPage(offerringService.count > 1 ? "serviceTypeSelection" : "serviceSelection")
  }
  const onAppointmentDataChange = (appointmentData: AppointmentData) => {
    if(!appointmentData.selectedServiceType) {
      setCurrentPage(offerringService.count === 1
        ? 'serviceSelection'
        : offerringService.count > 1
        ? 'serviceTypeSelection'
        : 'serviceSelection')
        
        setAppointmentData({ ...appointmentData, selectedServiceType : offerringService.selectedOffering });
    } else {
      setAppointmentData({ ...appointmentData });
    }
  };

  const onFormChange = async (appointmentData: AppointmentData) => {
    setAppointmentData({ ...appointmentData });
    setCurrentPage('confirmation');
  };
  const hideInfoPanel =
    !userContext?.isLandscape &&
    (currentPage == 'sign-up' ||
      currentPage == 'forgot-password' ||
      currentPage == 'email-verification');

  const showExtraInfo = currentPage == 'appointment-confirmation' ? false : true;
  const onLoginSuccess = async (
    page:
      | 'serviceSelection'
      | 'addons-selection'
      | 'personalInfo'
      | 'confirmation'
      | 'login'
      | 'forgot-password'
      | 'email-verification'
      | 'sign-up'
      | 'sign-in'
      | 'parent-profile'
      | 'pet-profile'
      | 'payment-method'
      | 'appointment-confirmation'
      | undefined
  ) => {
    await refetchUser();
    if (page) {
      setCurrentPage(page);
    }
  };
  const updateCurrentPage = () => {
    if(!appointmentData.selectedServiceType) {
      return offerringService.count > 1 ? 'serviceTypeSelection' : 'serviceSelection';
    }
    if (user && user.email) {
      if (
        (appointmentData.selectedServiceType === Utils.SERVICE_TYPES.GROOMING &&
          appointmentData?.service &&
          appointmentData.slot) ||
        (appointmentData.selectedServiceType === Utils.SERVICE_TYPES.DAY_CARE &&
          appointmentData.slot) ||
        (appointmentData.selectedServiceType === Utils.SERVICE_TYPES.BOARDING &&
          appointmentData.startDate &&
          appointmentData.endDate)
      ) {
        if (user.name && user.address && user.zip) {
          if (user.pets && user.pets.length > 0 && appointmentData.petProfile) {
            return 'payment-method';
          }
          return 'pet-profile';
        }
        return 'parent-profile';
      }
      return offerringService.count > 1 ? 'serviceTypeSelection' : 'serviceSelection';
    } else {
      return 'login';
    }
  };
  const onPaymentCardSuccess = async (appointmentData?: AppointmentData) => {
    if (appointmentData) {
      onAppointmentDataChange(appointmentData);
    } else {
      await refetchUser();
    }
    setCurrentPage('appointment-confirmation');
  };

  const createAppointment = async (
    appointmentData: AppointmentData,
    isNonRegistration?: boolean
  ) => {
    setAppointmentData({ ...appointmentData });
    const duration = Utils.getDurationFromService(appointmentData?.service);
    const requestData: BookAppointmentDTO = {
      data: {
        name: appointmentData.userData?.name ? appointmentData.userData?.name : '',
        email: appointmentData.userData?.email ? appointmentData.userData?.email.toLowerCase() : '',
        phone: appointmentData.userData?.phone ? appointmentData.userData?.phone : '',
        altEmails: appointmentData.userData?.altEmails ? appointmentData.userData?.altEmails : [],
        startTime: appointmentData?.slot?.start
          ? new Date(appointmentData?.slot?.start)
          : undefined,
        endTime: appointmentData?.slot?.end ? new Date(appointmentData?.slot?.end) : undefined,
        businessUserId: Number(appointmentData?.provider?.id),
        serviceId: Number(appointmentData?.service?.id),
        summary: appointmentData?.service?.name,
        description: `${appointmentData?.service?.name} for ${Utils.minutesToDhm(duration)}`,
        tz: appointmentData?.tz ? appointmentData?.tz : moment.tz.guess(true),
        businessId: hostBusinessData?.id,
        paymentMethodId:
          appointmentData?.paymentCard?.stripePaymentToken ||
          appointmentData?.paymentCard?.squareCardId ||
          appointmentData?.creditCard?.stripePaymentToken ||
          appointmentData?.creditCard?.squareCardId ||
          '',
        petDetails: appointmentData.petProfiles,
        addonSids: Utils.getSelectedAddonsIds(appointmentData.selectedAddons),
        duration: appointmentData.duration,
        startDate: moment(appointmentData.startDate).format('YYYY-MM-DD'),
        endDate: moment(appointmentData.endDate).format('YYYY-MM-DD'),
        petCount: appointmentData?.noOfPets ? appointmentData?.noOfPets : 0
      },
      serviceType: appointmentData.selectedServiceType,
    };

    try {
      let bookingResponse = await bookAppointmentMutation.mutateAsync(requestData);
      if (bookingResponse.paymentIntent && bookingResponse.paymentIntent.next_action && stripe) {
        const { error: confirmError, paymentIntent }: any = await stripe.confirmCardPayment(
          bookingResponse.paymentIntent.client_secret,
          {
            payment_method: bookingResponse.paymentIntent.payment_method,
          }
        );
        if (confirmError) {
          console.error(confirmError);
          requestData.data.paymentIntentId = bookingResponse.paymentIntent.id;
          bookingResponse = await bookAppointmentMutation.mutateAsync(requestData);
          setCurrentPage('confirmation');
        } else {
          requestData.data.paymentIntentId = paymentIntent.id;
          bookingResponse = await bookAppointmentMutation.mutateAsync(requestData);
          setCurrentPage('confirmation');
        }
      } else {
        setCurrentPage('confirmation');
      }
      if (
        appointmentData.selectedServiceType === Utils.SERVICE_TYPES.BOARDING &&
        bookingResponse.appointment.startTime
      ) {
        setAppointmentData({
          ...appointmentData,
          startDate: new Date(bookingResponse.appointment.startTime),
          endDate: new Date(bookingResponse.appointment.endTime),
          petProfiles : bookingResponse.appointment.pets,
          intakeFormLink: bookingResponse.appointment.intakeFormLink,
        });
      } else {
        setAppointmentData({
          ...appointmentData,
          petProfiles : bookingResponse.appointment.pets,
          intakeFormLink: bookingResponse.appointment.intakeFormLink,
        });
      }
    } catch (e:any) {
      console.log("error ", e.response.data);
      if(e.response.data.errors && e.response.data.errors.length) {
        const error = e.response.data.errors[0];
        if(error && error.code === "ERR063") {
          if (isNonRegistration && appointmentData) {
            setAppointmentData({
              ...appointmentData,
              paymentCard: undefined,
              intakeFormLink: appointmentData.intakeFormLink,
            });
            setCurrentPage('capture-payment');
          }
        }
      }
    }
  };
  const onBookingConfirmation = (data: AppointmentData) => {
    onAppointmentDataChange(data);
    setCurrentPage('confirmation');
  };
  const onSlotSelect = (hasAddons = false) => {
    if (hasAddons) {
      setCurrentPage('addons-selection');
    } else {
      appointmentData?.selectedLocation?.registrationRequired
        ? setCurrentPage('parent-profile')
        : setCurrentPage('personalInfo');
    }
  };
  const onScheduleAppointment = (appointmentData: AppointmentData) => {
    onAppointmentDataChange(appointmentData);
    const servicePrice = Utils.getPriceFromService(appointmentData.service);
    if (appointmentData.selectedServiceType === Utils.SERVICE_TYPES.GROOMING) {
      if (
        ((appointmentData?.selectedLocation?.advBookingPercentage &&
        parseInt(appointmentData?.selectedLocation?.advBookingPercentage) > 0) || (appointmentData?.selectedLocation?.advBookingAmount &&
          parseInt(appointmentData?.selectedLocation?.advBookingAmount) > 0)) &&
        appointmentData?.selectedLocation?.reservationType != 'REQUEST'
      ) {
        if (servicePrice > 0) {
          setCurrentPage('capture-payment');
        } else {
          setCurrentPage('pre-confirmation');
        }
      } else {
        createAppointment(appointmentData);
      }
    } else {
      if (
        ((appointmentData?.selectedLocation?.advBookingPercentage &&
        parseInt(appointmentData?.selectedLocation?.advBookingPercentage) > 0) || (appointmentData?.selectedLocation?.advBookingAmount &&
          parseInt(appointmentData?.selectedLocation?.advBookingAmount) > 0)) &&
        appointmentData?.selectedLocation?.reservationType != 'REQUEST'
      ) {
        if (appointmentData?.cost && appointmentData.cost?.fee > 0) {
          setCurrentPage('capture-payment');
        } else {
          setCurrentPage('pre-confirmation');
        }
      } else {
        createAppointment(appointmentData);
      }
    }
  };
  const cardSelectionSuccess = (data: AppointmentData) => {
    onAppointmentDataChange(data);
    setCurrentPage('pre-confirmation');
  };
  const onBackFromPreConfirmation = () => {
    if (appointmentData.selectedServiceType === Utils.SERVICE_TYPES.GROOMING) {
      const servicePrice = Utils.getPriceFromService(appointmentData.service);
      if (servicePrice > 0) {
        setCurrentPage('capture-payment');
      } else {
        setCurrentPage('personalInfo');
      }
    } else {
      if (appointmentData?.cost && appointmentData.cost?.fee > 0) {
        setCurrentPage('capture-payment');
      } else {
        setCurrentPage('personalInfo');
      }
    }
  };
  const appointmentServiceSelectionNext = (hasAddons: boolean) => {
    if (hasAddons) {
      setCurrentPage('addons-selection');
    } else {
      setCurrentPage(updateCurrentPage());
    }
  };
  const appointmentFormBack = () => {
    setCurrentPage(appointmentData.addonsAvailable ? 'addons-selection' : 'serviceSelection');
  };
  const serviceTypeSelectionNext = () => {
    setCurrentPage('serviceSelection');
  };
console.log(" current page ", currentPage);

  return (
    <section
      className={`petsked-container-bg schedule-appointment ${
        currentPage == 'confirmation' ? 'scroll' : 'no-scroll'
      }`}
      id="schedule-appointment"
    >
      {hostBusinessData ? (
        <>
          {currentPage !== 'confirmation' ? (
            <>
              {offerringService.count > 1 && (
                <>
                  {(userContext?.isLandscape && currentPage != 'serviceTypeSelection') ||
                  (!userContext?.isLandscape &&
                    (currentPage == 'serviceSelection' ||
                      currentPage == 'serviceTypeSelection')) ? (
                    currentPage != 'serviceTypeSelection' ? (
                      <div className={`schedule-header header-${currentPage}`}>
                        <BackNav onClick={backToPreviousPage} />
                        <PetskedTitle
                          title={
                            appointmentData.selectedServiceType === 'grooming'
                              ? t('Schedule an')
                              : t('Schedule a')
                          }
                          highlightTitle={
                            appointmentData.selectedServiceType === 'grooming'
                              ? t('appointment')
                              : appointmentData.selectedServiceType === 'day-care'
                              ? t('Daycare')
                              : t('Boarding')
                          }
                          size="lg"
                          alignText="left"
                          description=""
                        />
                      </div>
                    ) : (
                      currentPage == 'serviceTypeSelection' && (
                        <PetskedTitle
                          title={t("Hey,")}
                          highlightTitle={t("Welcome !")}
                          size="lg"
                          alignText="left"
                          description={t("What'd you like to have for your furry mate today?")}
                        />
                      )
                    )
                  ) : (
                    currentPage != 'capture-payment' && <BackNav onClick={backToPreviousPage} />
                  )}
                </>
              )}
              {offerringService.count === 1 && (
                <>
                  {userContext?.isLandscape ||
                  (!userContext?.isLandscape && currentPage == 'serviceSelection') ? (
                    <PetskedTitle
                      title={
                        appointmentData.selectedServiceType === 'grooming'
                          ? t('Schedule an')
                          : t('Schedule a')
                      }
                      highlightTitle={
                        appointmentData.selectedServiceType === 'grooming'
                          ? t('appointment')
                          : appointmentData.selectedServiceType === 'day-care'
                          ? t('Daycare')
                          : t('Boarding')
                      }
                      size="lg"
                      alignText="left"
                      description=""
                    />
                  ) : (
                    currentPage != 'capture-payment' && <BackNav onClick={backToPreviousPage} />
                  )}
                </>
              )}

              {hostBusinessData && appointmentData?.selectedLocation && (
                <>
                  {appointmentData?.selectedLocation?.registrationRequired ? (
                    <div
                      className={`details ${currentPage}-screen ${
                        (!userContext?.isLandscape && currentPage == 'login') ||
                        currentPage == 'parent-profile' ||
                        currentPage == 'pet-profile' ||
                        currentPage == 'payment-method'
                          ? 'swap'
                          : ''
                      }`}
                    >
                      {!hideInfoPanel && (
                        <AppointmentInfoPanel
                          showExtraInfo={showExtraInfo}
                          business={hostBusinessData}
                          data={appointmentData}
                          onChange={onLocationChange}
                        />
                      )}
                      <div className="right-pages-schedule" id="righ-side-scroller">
                        {currentPage == 'serviceTypeSelection' && (
                          <ServiceTypeSelection
                            appointmentData={appointmentData}
                            business={hostBusinessData}
                            onChange={onAppointmentDataChange}
                            next={serviceTypeSelectionNext}
                          />
                        )}

                        {currentPage == 'serviceSelection' && (
                          <AppointmentServiceSelection
                            appointmentData={appointmentData}
                            business={hostBusinessData}
                            onChange={onAppointmentDataChange}
                            next={appointmentServiceSelectionNext}
                          />
                        )}

                        {currentPage == 'addons-selection' && (
                          <SelectAddonServices
                            onChange={onAppointmentDataChange}
                            business={hostBusinessData}
                            appointmentData={appointmentData}
                            onNext={() => setCurrentPage(updateCurrentPage())}
                            onBackClick={() => setCurrentPage('serviceSelection')}
                          />
                        )}

                        {currentPage == 'login' && (
                          <ConsumerLogin
                            onBack={(page) => setCurrentPage(page)}
                            onNext={onLoginSuccess}
                          />
                        )}
                        {currentPage == 'forgot-password' && (
                          <ForgotPassword
                            onBack={(page) => setCurrentPage(page)}
                            onNext={(page) => setCurrentPage(page)}
                          />
                        )}
                        {currentPage == 'sign-up' && (
                          <ConsumerSignUp
                            onBack={(page) => setCurrentPage(page)}
                            onNext={(page) => setCurrentPage(page)}
                          />
                        )}
                        {currentPage == 'email-verification' && (
                          <EmailVerification
                            onBack={(page) => setCurrentPage(page)}
                            onNext={(page) => setCurrentPage(page)}
                          />
                        )}
                        {currentPage == 'parent-profile' && (
                          <ParentProfile
                            onNext={(page) => {
                              setCurrentPage(page);
                            }}
                            onBackClick={() => {
                              setCurrentPage('serviceSelection');
                            }}
                          />
                        )}
                        {currentPage == 'pet-profile' && (
                          <PetProfile
                            appointmentData={appointmentData}
                            onSuccess={() => {}}
                            onNext={(page, appointmentData: AppointmentData) => {
                              setCurrentPage(page);
                              onAppointmentDataChange(appointmentData);
                            }}
                            onBack={(page) => {
                              setCurrentPage(page);
                            }}
                          />
                        )}
                        {currentPage == 'payment-method' && (
                          <CreditCardDetails
                            appointmentData={appointmentData}
                            onSuccess={onPaymentCardSuccess}
                            onBack={(page) => {
                              setCurrentPage(page);
                            }}
                            paymentMode={hostBusinessData.paymentMode}
                          />
                        )}

                        {currentPage == 'appointment-confirmation' && (
                          <Elements stripe={stripePromise}>
                            <BookingConfirmation
                              onComplete={onBookingConfirmation}
                              data={appointmentData}
                              onBack={(page) => {
                                setCurrentPage(page);
                              }}
                              business={hostBusinessData}
                            />
                          </Elements>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className={`details ${currentPage}-screen`}>
                      <AppointmentInfoPanel
                        showExtraInfo={showExtraInfo}
                        business={hostBusinessData}
                        data={appointmentData}
                        onChange={onAppointmentDataChange}

                      />
                      <div className="right-pages-schedule" id="righ-side-scroller">
                        {currentPage == 'serviceTypeSelection' && hostBusinessData && (
                          <ServiceTypeSelection
                            appointmentData={appointmentData}
                            business={hostBusinessData}
                            onChange={onAppointmentDataChange}
                            next={serviceTypeSelectionNext}
                          />
                        )}
                        {currentPage == 'serviceSelection' && hostBusinessData && (
                          <AppointmentServiceSelection
                            appointmentData={appointmentData}
                            business={hostBusinessData}
                            onChange={onAppointmentDataChange}
                            next={onSlotSelect}
                          />
                        )}

                        {currentPage == 'addons-selection' && (
                          <SelectAddonServices
                            onChange={onAppointmentDataChange}
                            appointmentData={appointmentData}
                            business={hostBusinessData}
                            onNext={onSlotSelect}
                            onBackClick={() => setCurrentPage('serviceSelection')}
                          />
                        )}
                        {currentPage == 'personalInfo' && (
                          <AppointmentForm
                            business={hostBusinessData}
                            onSchedule={onScheduleAppointment}
                            appointmentData={appointmentData}
                            isLoading={bookAppointmentMutation.isLoading}
                            back={appointmentFormBack}
                          />
                        )}
                        {currentPage == 'capture-payment' && appointmentData && (
                          <CreditCardNonRegistration
                            appointmentData={appointmentData}
                            onSuccess={cardSelectionSuccess}
                            onBackClick={() => setCurrentPage('personalInfo')}
                            isLoading={bookAppointmentMutation.isLoading}
                            paymentMode={hostBusinessData.paymentMode}
                          />
                        )}
                        {currentPage == 'pre-confirmation' && appointmentData && (
                          <NonRegistrationConfirmation
                            appointmentData={appointmentData}
                            business={hostBusinessData}
                            onComplete={createAppointment}
                            onBack={onBackFromPreConfirmation}
                            isLoading={bookAppointmentMutation.isLoading}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <AppointmentFormConfirmation
                business={hostBusinessData}
                appointmentData={appointmentData}
              />
            </>
          )}
        </>
      ) : (
        <div className="preloader-schedule-new">
          <Lottie className="loader" animationData={appCompoLoading} />
        </div>
      )}
    </section>
  );
}
